import { Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { justifyContentSpaceBetween } from "../../../styles/mui/styles/display";
import colors from "../../../styles/colors/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import {
  BottomGrid,
  BottomGridMiddleContainer,
  BottomGridMiddleContainerField,
  MainLabelContainer,
  OuterBodyGrid,
  ReadOnlyFieldStyle,
  RightTopGrid,
  StateField,
} from "../styles/TabsStyle";
import { GeneralInfo as GeneralInfoModel } from "../../../models/AuthorizationDetailsTab";
import ReadOnlyField from "../../../components/formComponents/ReadOnlyField";
import { decryptId, getValue } from "../../../utils";
import { formatDate } from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import { ReadOnlyFieldValueStyle } from "../../../components/formComponents/styles/style";
import { useParams } from "react-router-dom";

export interface PropsFromState {
  generalInfoState: GeneralInfoModel;
}
export interface PropsFromDispatch {
  getGeneralInfo(referralId: number): void;
}
type AllProps = PropsFromState & PropsFromDispatch;

const GeneralInfo: React.FC<AllProps> = ({
  generalInfoState,
  getGeneralInfo,
}) => {

  const [referralId, setReferralId] = useState<number | null>(null);
  const { authNo } = useParams();
  type InfoFiled = {
    label: string;
    value: string | number;
    size: number;
    isState?: boolean;
  };

    useEffect(() => {
      const loadReferralId = async () => {
        if (!authNo) {
          setReferralId(null);
          return;
        }
  
        const decryptedId = await decryptId(authNo);
        setReferralId(Number(decryptedId));
      };
  
      void loadReferralId();
    }, [authNo]);

  const generalInfoFields = [
    {
      label: "Date/Time request Received:",
      value: formatDate(
        getValue(
          generalInfoState,
          "authorizationGeneralInfo.dateTimeRequestReceived"
        ),
        DateFormats.MM_DD_YYYY_comma_hh_mm_ss_a
      ),
      size: 6,
    },
    {
      label: "Authorization Type:",
      value: getValue(
        generalInfoState,
        "authorizationGeneralInfo.authorizationType"
      ),
      size: 6,
    },
    {
      label: "Created On:",
      value: formatDate(
        getValue(generalInfoState, "authorizationGeneralInfo.createdOn"),
        DateFormats.MM_DD_YYYY_comma_hh_mm_a
      ),
      size: 6,
    },
    {
      label: "Authorization Status:",
      value: getValue(
        generalInfoState,
        "authorizationGeneralInfo.authorizationStatus"
      ),
      size: 6,
    },
    {
      label: "Start of Care:",
      value: formatDate(
        getValue(generalInfoState, "authorizationGeneralInfo.startOfCareDate"),
        DateFormats.MM_DD_YYYY
      ),
      size: 6,
    },
    {
      label: "Urgency Status:",
      value: getValue(
        generalInfoState,
        "authorizationGeneralInfo.urgencyStatus"
      ),
      size: 6,
    },
    {
      label: "Effective From:",
      value: formatDate(
        getValue(generalInfoState, "authorizationGeneralInfo.effectiveFrom"),
        DateFormats.MM_DD_YYYY
      ),
      size: 12,
    },
    {
      label: "Effective Through:",
      value: formatDate(
        getValue(generalInfoState, "authorizationGeneralInfo.effectiveThrough"),
        DateFormats.MM_DD_YYYY
      ),
      size: 12,
    },
  ];

  const originalDiagnosisCodes =
    getValue(
      generalInfoState,
      "authorizationDiagnosisCodes.originalPrimaryDiagnosisCode"
    ) ?? [];

  const otherDiagnosisCodes =
    getValue(
      generalInfoState,
      "authorizationDiagnosisCodes.otherDiagnosisCode"
    ) ?? [];
  const addressFields = [
    {
      label: "Street 1:",
      value: getValue(generalInfoState, "serviceAddress.street1"),
      size: 6,
    },
    {
      label: "Street 2:",
      value: getValue(generalInfoState, "serviceAddress.street2"),
      size: 6,
    },
    {
      label: "City:",
      value: getValue(generalInfoState, "serviceAddress.city"),
      size: 4,
    },
    {
      label: "State:",
      value: getValue(generalInfoState, "serviceAddress.state"),
      size: 5,
      isState: true,
    },
    {
      label: "Zip:",
      value: getValue(generalInfoState, "serviceAddress.zipCode"),
      size: 2,
    },
  ];

  const providerFields = [
    {
      label: "Agency Name:",
      value: getValue(generalInfoState, "serviceProvider.agencyName"),
      size: 6,
    },
    {
      label: "Agency NPI:",
      value: getValue(generalInfoState, "serviceProvider.agencyNPI"),
      size: 6,
    },
    {
      label: "Agency Phone:",
      value: getValue(generalInfoState, "serviceProvider.agencyPhone"),
      size: 6,
    },
    {
      label: "Agency Fax:",
      value: getValue(generalInfoState, "serviceProvider.agencyFax"),
      size: 6,
    },
    {
      label: "Street 1:",
      value: getValue(generalInfoState, "serviceProvider.street1"),
      size: 6,
    },
    {
      label: "Street 2:",
      value: getValue(generalInfoState, "serviceProvider.street2"),
      size: 6,
    },
    {
      label: "City:",
      value: getValue(generalInfoState, "serviceProvider.city"),
      size: 4,
    },
    {
      label: "State:",
      value: getValue(generalInfoState, "serviceProvider.state"),
      size: 5,
      isState: true,
    },
    {
      label: "Zip:",
      value: getValue(generalInfoState, "serviceProvider.zipCode"),
      size: 3,
    },

  ];

  useEffect(() => {
    if (referralId !== null) getGeneralInfo(referralId);
  }, [referralId]);

  const renderFields = (fields: InfoFiled[]) =>
    fields.map((field) => (
      <Grid
        key={field.label}
        size={field.size}
        sx={field.isState ? StateField : BottomGridMiddleContainerField}
      >
        <ReadOnlyField label={field.label} value={field.value} />
      </Grid>
    ));
  const renderDiagnosisList = <T extends Record<string, string>>(
    list: T[] | null | undefined,
    codeKey: keyof T,
    descKey: keyof T
  ) => {
    if (!Array.isArray(list) || list.length === 0) {
      return <Typography></Typography>;
    }

    return list.map((item, index) => (
      <Grid key={index} size={12} sx={ReadOnlyFieldStyle}>
        <Typography
          fontWeight={fontWeight.Weight[4]}
          lineHeight="1.3"
          sx={ReadOnlyFieldValueStyle}
        >
          {item?.[codeKey]}
        </Typography>
        <Typography
          fontWeight={fontWeight.Weight[3]}
          lineHeight="1.3"
          sx={ReadOnlyFieldValueStyle}
        >
          -
        </Typography>
        <Typography
          fontWeight={fontWeight.Weight[3]}
          lineHeight="1.3"
          sx={ReadOnlyFieldValueStyle}
        >
          {item?.[descKey]}
        </Typography>
      </Grid>
    ));
  };

  return (
    <Grid container sx={OuterBodyGrid}>
      <Grid size={12}>
        <Grid container spacing={2} sx={justifyContentSpaceBetween}>
          <Grid size={3.9} sx={BottomGrid}>
            <Grid container sx={MainLabelContainer}>
              <Typography
                fontWeight={fontWeight.Weight[5]}
                color={colors.black[1]}
              >
                GENERAL ADMISSION INFORMATION
              </Typography>
            </Grid>
            <Grid container sx={BottomGridMiddleContainer}>
              {renderFields(generalInfoFields)}
            </Grid>
          </Grid>
          <Grid size={3.9} sx={BottomGrid}>
            <Grid container sx={MainLabelContainer}>
              <Typography
                fontWeight={fontWeight.Weight[5]}
                color={colors.black[1]}
              >
                DIAGNOSIS CODES
              </Typography>
            </Grid>
            <Grid container sx={BottomGridMiddleContainer}>
              <Grid size={12} sx={BottomGridMiddleContainerField}>
                <Typography
                  fontWeight={fontWeight.Weight[3]}
                  color={colors.fonts[2]}
                >
                  Original Primary Diagnosis Code:
                </Typography>
                <Typography
                  fontWeight={fontWeight.Weight[4]}
                  lineHeight="1.3"
                  sx={ReadOnlyFieldValueStyle}
                >
                  {renderDiagnosisList(
                    originalDiagnosisCodes,
                    "originalDiagnosisCode",
                    "originalDiagnosisCodeDescription"
                  )}
                </Typography>
              </Grid>
              <Grid size={12} sx={BottomGridMiddleContainerField}>
                <Typography
                  fontWeight={fontWeight.Weight[3]}
                  color={colors.fonts[2]}
                >
                  Other Diagnosis Code:
                </Typography>
                <Typography
                  fontWeight={fontWeight.Weight[4]}
                  lineHeight="1.3"
                  sx={ReadOnlyFieldValueStyle}
                >
                  {renderDiagnosisList(
                    otherDiagnosisCodes,
                    "diagnosisCode",
                    "diagnosisCodeDescription"
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={3.9}>
            <Grid container>
              <Grid size={12} sx={RightTopGrid}>
                <Grid container sx={MainLabelContainer}>
                  <Typography
                    fontWeight={fontWeight.Weight[5]}
                    color={colors.black[1]}
                  >
                    SERVICE ADDRESS
                  </Typography>
                </Grid>
                <Grid container sx={BottomGridMiddleContainer}>
                  {renderFields(addressFields)}
                </Grid>
              </Grid>
              <Grid size={12} sx={BottomGrid}>
                <Grid container sx={MainLabelContainer}>
                  <Typography
                    fontWeight={fontWeight.Weight[5]}
                    color={colors.black[1]}
                  >
                    SERVICE PROVIDER
                  </Typography>
                </Grid>
                <Grid container sx={BottomGridMiddleContainer}>
                  {renderFields(providerFields)}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default GeneralInfo;
